﻿import React from "react";
import parser from "html-react-parser";
import { observer } from "mobx-react";
import type { IResourcesStore } from "../../../../resources/ResourcesStore";
import { injectFromApp } from "../../../../shared-components/inject";
import type { IUploadStore } from "../../../UploadStore";
import { injectFromAppUpload } from "../../../inject";
import type { PatientToUpload } from "../../../PatientToUpload";
import { StudyInUploadComponent } from "../study/StudyInUploadComponent";
import { UploadStatus } from "../../../FileUpload";
import { RichToolTipComponent } from "../../../../shared-components/rich-tooltip/RichToolTipComponent";
import { FileUploadComponent } from "../../file/FileUploadComponent";
import { UploadButtonLogicComponent } from "../button-and-status-logic/UploadButtonLogicComponent";
import { UploadProgressComponent } from "../button-and-status-logic/status-and-progress/UploadProgressComponent";

interface IPatientToUploadComponent {
    resourcesStore?: IResourcesStore;
    uploadStore?: IUploadStore;
    patientToUpload: PatientToUpload;
}

@injectFromApp<IPatientToUploadComponent>((stores) => ({
    resourcesStore: stores.resourcesStore,
}))

@injectFromAppUpload<IPatientToUploadComponent>((stores) => ({
    uploadStore: stores.uploadStore,
}))

@observer
export class PatientToUploadComponent extends React.Component<IPatientToUploadComponent> {
    state = {
        detailedInfoOpen: false,
    };

    toggleShowFiles = (event): void => {
        this.setState({
            detailedInfoOpen: !this.state.detailedInfoOpen
        });
    };

    removePatientToUpload = (event): void => {
        event.stopPropagation();
        const { uploadStore, patientToUpload } = this.props;
        uploadStore!.removePatientToUpload(patientToUpload);
    };

    onEmailAddressChanged = (event): void => {
        const { patientToUpload } = this.props;
        patientToUpload.setEmailAddress(event.target.value);
    };

    onMobilePhoneChanged = (event): void => {
        const { patientToUpload } = this.props;
        patientToUpload.setMobilePhone(event.target.value);
    };

    componentWillUnmount() {
        const { patientToUpload } = this.props;
        patientToUpload.dispose();
    }

    setPrivacyPolicyConsent = (event): void => {
        const { patientToUpload } = this.props;
        patientToUpload.setPrivacyPolicyConsent(event.target.checked);
    };

    setPatientConsent = (event): void => {
        const { patientToUpload } = this.props;
        patientToUpload.setPatientConsent(event.target.checked);
    };

    render() {
        const { resourcesStore, patientToUpload } = this.props;
        const { resources } = resourcesStore!;
        const { patientId, patientName, studies, status, canBeRemoved, filesToUpload, patientDobFormatted, emailAddress, emailAddressIsValid, mobilePhone } = patientToUpload;
        const { detailedInfoOpen } = this.state;

        return (
            <div
                className={
                    "patient-to-upload-container" +
                    (status === UploadStatus.Paused ? " paused" : "") +
                    (status === UploadStatus.Failed ? " failed" : "") +
                    (status === UploadStatus.RateLimited ? " rate-limited" : "") +
                    (status === UploadStatus.Started ? " started" : "") +
                    (status === UploadStatus.Done ? " done" : "") +
                    (this.state.detailedInfoOpen ? " open-detailed-file-information" : "")
                }
            >
                <h3 className={ "patient-name" }>{ patientName }</h3>

                <div className={ "patient-and-studies-container" }>
                    <div className={ "row" }>
                        <div className={ "patient-information-container label-value-containers-holder" }>
                            <div className={ "label-value-container" }>
                                <div className={ "label-item" }>{ resources.DateOfBirth }</div>
                                <div className={ "value-item" }>{ patientDobFormatted }</div>
                            </div>
                            <div className={ "label-value-container" }>
                                <div className={ "label-item" }>{ resources.PatientID }</div>
                                <div className={ "value-item" }>{ patientId }</div>
                            </div>
                            <div className={ "label-value-container optional-information" }>
                                <div className={ "label-item" }>{ resources.OptionalPatientInformation }</div>
                                <input
                                    type={ "text" }
                                    className={ "input-item email " + (emailAddressIsValid ? "" : "error") }
                                    value={ emailAddress }
                                    onChange={ this.onEmailAddressChanged }
                                    name={ "patient-email-address" }
                                    data-lpignore={ true }
                                    autoComplete={ "off" }
                                    disabled={ !canBeRemoved }
                                    placeholder={ resources.EmailAddress }
                                />

                                <input
                                    type={ "text" }
                                    className={ "input-item telephone" }
                                    name={ "patient-mobile-phone" }
                                    value={ mobilePhone }
                                    onChange={ this.onMobilePhoneChanged }
                                    data-lpignore={ true }
                                    autoComplete={ "off" }
                                    disabled={ !canBeRemoved }
                                    placeholder={ resources.MobilePhone }
                                />
                            </div>
                        </div>
                        <div className={ "studies-information-container" }>
                            { studies.map((s, i) => (
                                <StudyInUploadComponent studyInUpload={ s } patientToUpload={ patientToUpload } canBeRemoved={ canBeRemoved } key={ s.studyGuid }/>
                            )) }
                        </div>
                    </div>
                    <div className={ "privacy-checkbox-container" }>
                        <div>
                            <label>
                                <input type="checkbox" disabled={ !canBeRemoved } onChange={ this.setPrivacyPolicyConsent }/>
                                { parser(resources.PrivacyPolicyConsent.replace("<a>", "<a href=\"/POW4/Legal\" target=_blank>")) }
                            </label>
                        </div>
                        <div>
                            <label>
                                <input type="checkbox" disabled={ !canBeRemoved } onChange={ this.setPatientConsent }/>
                                { resources.PatientConsent }
                            </label>
                        </div>
                    </div>
                </div>

                <div className={ "button-and-status-container" }>
                    <UploadButtonLogicComponent patientToUpload={ patientToUpload }>
                        <UploadProgressComponent patientToUpload={ patientToUpload }/>
                    </UploadButtonLogicComponent>
                    <p className={ "small-text toggle-detailed-file-information" } onClick={ this.toggleShowFiles }>
                        { detailedInfoOpen ? resources.HideDetailedFileInformation : resources.ShowDetailedFileInformation }
                    </p>
                </div>

                { this.state.detailedInfoOpen && (
                    <div className={ "patient-files-container" }>
                        { filesToUpload.map((f) => (
                            <FileUploadComponent key={ f.fileGuid } file={ f } patientToUpload={ patientToUpload }/>
                        )) }
                    </div>
                ) }

                { canBeRemoved && (
                    <RichToolTipComponent toolTipResource={ "RemovePatient" } onClick={ this.removePatientToUpload } className={ "remove-patient" }>
                        &nbsp;
                    </RichToolTipComponent>
                ) }
            </div>
        );
    }
}
